import React, { useContext, useEffect, useRef, useState } from 'react';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import Button from '@mui/material/Button';
import {
  usePopupState,
  bindHover,
  bindPopover,
  PopupState
} from 'material-ui-popup-state/hooks';
import MenuItem from '@mui/material/MenuItem';
import Link from '@mui/material/Link';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box } from '@mui/system';
import {
  advisorMenuData,
  articlesMenuData,
  horoscopeMenuData
} from './menuData';
import { MenuContainer } from './styled';
import { PrimaryHeaderContext } from '../PrimaryHeader/PrimaryHeader';

const MenuButton = ({
  menuText,
  popupState,
  calcAndSetMenuBottom
}: {
  menuText: string;
  popupState: PopupState;
  calcAndSetMenuBottom: (isForceCalc?: boolean) => void;
}) => (
  <Button
    onPointerEnter={() => calcAndSetMenuBottom(true)}
    {...bindHover(popupState)}
    endIcon={<KeyboardArrowDownIcon />}
    sx={{
      color: 'white',
      padding: 0,
      marginTop: '10px',
      marginLeft: '25px',
      '&:hover': {
        background: 'none !important'
      }
    }}>
    {menuText}
  </Button>
);

const MenuHoverPopOver = ({
  menuLinks,
  menuRef,
  menuBottom,
  popupState
}: {
  menuLinks: NavigationItem[];
  menuRef: React.MutableRefObject<HTMLInputElement>;
  menuBottom: number;
  popupState: PopupState;
}) => {
  const primaryHeaderContext = useContext(PrimaryHeaderContext);
  const { trackMixpanelEvent } = primaryHeaderContext;

  const onMenuOptionClick = ({ isTrackEvent, name }: NavigationItem) => {
    if (isTrackEvent && trackMixpanelEvent)
      trackMixpanelEvent('Main Category Clicked', { category: name }, true);
  };

  return (
    <HoverPopover
      ref={menuRef}
      disableScrollLock
      PaperProps={{
        sx: {
          background: 'none',
          border: 'none',
          boxShadow: 'none',
          marginTop: `${menuBottom + 10}px !important`,
          top: '0 !important',
          overflow: 'visible'
        }
      }}
      {...bindPopover(popupState)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }}>
      <Box
        sx={{
          backgroundColor: 'white',
          pointerEvents: 'auto',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          marginTop: '20px',
          padding: '10px 0',
          '& .MuiMenuItem-root': {
            borderLeft: '7px solid white'
          },
          '& .MuiMenuItem-root:hover': {
            borderLeft: '7px solid #09b3b0',
            backgroundColor: 'rgba(186,232,231,.3)'
          },
          '& .MuiMenuItem-root:focus': {
            borderLeft: '7px solid #09b3b0',
            backgroundColor: 'rgba(186,232,231,.3)'
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'relative',
            marginX: 'auto',
            marginTop: '-10px',
            width: 18,
            height: 18,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0
          }
        }}>
        {menuLinks.map((link: NavigationItem) => (
          <MenuItem sx={{ padding: 0 }} key={link.name} href={link.href} dense>
            <Link
              onClick={() => onMenuOptionClick(link)}
              key={link.name}
              href={link.href}
              underline="none"
              sx={{
                display: 'inline-block',
                width: '100%',
                color: 'black',
                padding: '11px 26px 12px 26px',
                fontSize: '18px',
                lineHeight: 1
              }}>
              {link.name}
            </Link>
          </MenuItem>
        ))}
      </Box>
    </HoverPopover>
  );
};

export default function MainMenu() {
  const [menuBottom, setMenuBottom] = useState<number>(-1);
  const containerRef = useRef<HTMLInputElement>(null);

  const advisorMenuRef = useRef<HTMLInputElement>(null);
  const articlesMenuRef = useRef<HTMLInputElement>(null);
  const horoscopeMenuRef = useRef<HTMLInputElement>(null);

  const advisorMenuState = usePopupState({
    variant: 'popover',
    popupId: advisorMenuData.name
  });
  const articlesMenuState = usePopupState({
    variant: 'popover',
    popupId: articlesMenuData.name
  });
  const horoscopeMenuState = usePopupState({
    variant: 'popover',
    popupId: horoscopeMenuData.name
  });

  const isPopOverOpen = () =>
    [advisorMenuRef, articlesMenuRef, horoscopeMenuRef].find(
      (refElem) => refElem.current
    );

  const calcAndSetMenuBottom = (isForceCalc?: boolean) => {
    if (!isPopOverOpen() && !isForceCalc) return;
    const { bottom } = containerRef.current.getBoundingClientRect();
    if (bottom !== menuBottom) setMenuBottom(bottom);
  };

  useEffect(() => {
    if (!containerRef.current) return;
    setMenuBottom(containerRef.current.getBoundingClientRect().bottom);
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', () => calcAndSetMenuBottom());
    return () => {
      document.removeEventListener('scroll', () => calcAndSetMenuBottom());
    };
  }, []);

  return (
    <MenuContainer ref={containerRef}>
      <MenuButton
        menuText={advisorMenuData.name}
        calcAndSetMenuBottom={calcAndSetMenuBottom}
        popupState={advisorMenuState}
      />
      <MenuHoverPopOver
        menuBottom={menuBottom}
        menuLinks={advisorMenuData.links}
        menuRef={advisorMenuRef}
        popupState={advisorMenuState}
      />

      <MenuButton
        menuText={articlesMenuData.name}
        calcAndSetMenuBottom={calcAndSetMenuBottom}
        popupState={articlesMenuState}
      />
      <MenuHoverPopOver
        menuBottom={menuBottom}
        menuLinks={articlesMenuData.links}
        menuRef={articlesMenuRef}
        popupState={articlesMenuState}
      />

      <MenuButton
        menuText={horoscopeMenuData.name}
        calcAndSetMenuBottom={calcAndSetMenuBottom}
        popupState={horoscopeMenuState}
      />
      <MenuHoverPopOver
        menuBottom={menuBottom}
        menuLinks={horoscopeMenuData.links}
        menuRef={horoscopeMenuRef}
        popupState={horoscopeMenuState}
      />
    </MenuContainer>
  );
}
